import { omit } from 'lodash';
import { memo, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { useDataProvider } from '@work4all/data';
import { useDataMutation } from '@work4all/data/lib/hooks/data-provider/useDataMutation';

import { COLUMNS_BY_GENERAL_SETTINGS_TYPE } from '@work4all/models/lib/constants';
import { DataRequest } from '@work4all/models/lib/DataProvider';
import { EMode } from '@work4all/models/lib/Enums/EMode.enum';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { MaskTab, MaskTabPanel, MaskTabs } from '../../../mask-tabs';
import { MaskContent } from '../../components/MaskContent/MaskContent';
import { useMaskConfig } from '../../hooks/mask-context';
import { OverlayController } from '../../overlay-controller/OverlayController';
import { useMaskOverlay } from '../../overlay-controller/use-mask-overlay';
import { MaskControllerProps } from '../../types';
import { pickUpdateFields } from '../../utils/pick-update-fields';

import { GeneralTabPanel } from './tabs/GeneralTabPanel';
import {
  ExtendedGeneralSettingsEntity,
  GeneralSettingsEntity,
  GeneralSettingsMaskFormValue,
} from './types';

/**
 * Creates entity-specific request data including additional columns from COLUMNS_BY_GENERAL_SETTINGS_TYPE
 */
function createRequestData(entityType: Entities): Record<string, null> {
  const baseFields = { id: null, name: null };
  const additionalColumns = COLUMNS_BY_GENERAL_SETTINGS_TYPE[entityType] || [];

  const additionalFields = Object.fromEntries(
    additionalColumns.map((column) => [column, null])
  );

  return { ...baseFields, ...additionalFields };
}

/**
 * Creates entity-specific new entity data including additional columns from COLUMNS_BY_GENERAL_SETTINGS_TYPE
 */
function createNewEntityData<T extends GeneralSettingsEntity>(
  entityType: Entities
): T & ExtendedGeneralSettingsEntity {
  const baseFields = { id: undefined, name: '' };
  const additionalColumns = COLUMNS_BY_GENERAL_SETTINGS_TYPE[entityType] || [];

  const additionalFields = Object.fromEntries(
    additionalColumns.map((column) => [column, ''])
  );

  return { ...baseFields, ...additionalFields } as T &
    ExtendedGeneralSettingsEntity;
}

interface GeneralSettingsOverlayControllerProps<
  T extends ExtendedGeneralSettingsEntity
> extends MaskControllerProps {
  entity: Entities;
  requestData?: DataRequest['data'];
  newEntityData?: T;
  skip?: boolean;
}

export const GeneralSettingsOverlayController = <
  T extends ExtendedGeneralSettingsEntity
>({
  entity,
  requestData,
  newEntityData,
  skip = false,
  ...props
}: GeneralSettingsOverlayControllerProps<T>) => {
  // Use dynamic request data that includes entity-specific columns
  const entityRequestData = useMemo(() => {
    return requestData || createRequestData(entity);
  }, [requestData, entity]);

  // Use dynamic new entity data that includes entity-specific columns
  const entityNewData = useMemo(() => {
    return newEntityData || createNewEntityData<T>(entity);
  }, [newEntityData, entity]);
  const mask = useMaskConfig({ ...props, entity });

  const request = useMemo<DataRequest>(() => {
    const filter = [{ id: { $eq: mask.id } }];
    return { filter, entity, data: entityRequestData };
  }, [entity, entityRequestData, mask?.id]);

  const dataRequest = useMemo(() => {
    return {
      entity,
      completeDataResponse: true,
      data: entityRequestData,
    };
  }, [entity, entityRequestData]);

  const data = useDataProvider<T>(dataRequest, skip);

  const normalizeData = useCallback(
    (
      entity: GeneralSettingsMaskFormValue,
      newEntity: GeneralSettingsMaskFormValue,
      isCreateMode: boolean
    ) => {
      if (isCreateMode) return newEntity;

      return data?.data?.find(
        (x) => `${x.id}` === `${mask?.id}`
      ) as unknown as ExtendedGeneralSettingsEntity;
    },
    [data?.data, mask?.id]
  );

  const overlay = useMaskOverlay<GeneralSettingsMaskFormValue>({
    entity,
    request,
    newEntityData: entityNewData,
    mask,
    normalizeData: skip ? undefined : normalizeData,
    getSubTitle: (x) => x.name,
  });
  const { form } = overlay;

  const [mutate] = useDataMutation<GeneralSettingsMaskFormValue, EMode.upsert>({
    entity: mask.entity,
    mutationType: EMode.upsert,
    responseData: request.data as ExtendedGeneralSettingsEntity,
    onCompleted: (data) => {
      if (mask.isCreateMode) {
        props.onAfterSave(data);
      } else {
        props.onAfterSave(data);
      }
    },
  });

  const handleSubmit = useCallback(
    async (values: GeneralSettingsMaskFormValue) => {
      const updateRaw = mask.isCreateMode
        ? values
        : pickUpdateFields(values, form.formState.dirtyFields);

      await mutate(omit(updateRaw, ['__typename']));
    },
    [mask.isCreateMode, form.formState.dirtyFields, mutate]
  );

  return (
    <OverlayController<GeneralSettingsMaskFormValue>
      {...overlay}
      onSubmit={handleSubmit}
      tabs={<GeneralSettingsMaskTabs />}
    >
      <GeneralSettingsMaskContent />
    </OverlayController>
  );
};

const GeneralSettingsMaskTabs = memo(function GeneralSettingsMaskTabs() {
  const { t } = useTranslation();

  return (
    <MaskTabs>
      <MaskTab value="general" label={t('MASK.GENERAL')} />
    </MaskTabs>
  );
});

const GeneralSettingsMaskContent = memo(function GeneralSettingsMaskContent() {
  return (
    <MaskContent>
      <MaskTabPanel value="general">
        <GeneralTabPanel />
      </MaskTabPanel>
    </MaskContent>
  );
});
